import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useBills, useCreateBill, useDeleteBill, useUpdateBill } from "../lib/hooks";
import { formatMoney, CATEGORIES, FREQUENCIES, CURRENCIES } from "../lib/finance";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { usePaywall } from "../components/Paywall";

const FREE_BILL_LIMIT_MSG = "5 bills max on Free tier";

const empty = { name: "", category: "Other", amount: "", first_due_date: new Date().toISOString().slice(0, 10), frequency: "monthly", autopay: false, notes: "" };

export default function BillsPage() {
  const { user } = useAuth();
  const currency = user?.prefs?.currency || "USD";
  const { data: bills = [] } = useBills();
  const createBill = useCreateBill();
  const updateBill = useUpdateBill();
  const deleteBill = useDeleteBill();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const paywall = usePaywall();

  const openNew = () => {
    setEditing(null); setForm(empty); setOpen(true);
  };
  const openNewGated = () => {
    if (!paywall.isPaid && bills.length >= 5) {
      paywall.gateQuota("5 bills max on Free tier");
      return;
    }
    openNew();
  };
  const openEdit = (b) => { setEditing(b.bill_id); setForm({ ...b }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) };
    if (!payload.name || !payload.amount) { toast.error("Name and amount are required"); return; }
    try {
      if (editing) { await updateBill.mutateAsync({ id: editing, data: payload }); toast.success("Bill updated"); }
      else { await createBill.mutateAsync(payload); toast.success("Bill added"); }
      setOpen(false);
    } catch (err) {
      if (err?.response?.status === 402) {
        setOpen(false);
        paywall.gateQuota(`${FREE_BILL_LIMIT_MSG}`);
        return;
      }
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this bill and all its paid marks?")) return;
    await deleteBill.mutateAsync(id);
    toast.success("Bill deleted");
  };

  const filtered = filter === "all" ? bills : bills.filter((b) => b.frequency === filter);
  const symbol = CURRENCIES[currency]?.symbol || "$";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="bills-page">
      {paywall.Modal}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Bills</h1>
          <p className="text-sm text-muted-foreground mt-1">Recurring & one-time expenses. Set it once, mark paid per occurrence.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px]" data-testid="bills-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All frequencies</SelectItem>
              {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewGated} data-testid="add-bill-btn"><Plus size={16} className="mr-1"/> Add bill</Button>
            </DialogTrigger>
            <DialogContent data-testid="bill-dialog">
              <DialogHeader><DialogTitle>{editing ? "Edit bill" : "New bill"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="bill-form-name-input" required/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount ({symbol})</Label>
                    <Input type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} data-testid="bill-form-amount-input" required/>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
                      <SelectTrigger data-testid="bill-form-category-select"><SelectValue/></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>First due date</Label>
                    <Input type="date" value={form.first_due_date} onChange={(e)=>setForm({...form, first_due_date: e.target.value})} data-testid="bill-form-date-input" required/>
                  </div>
                  <div>
                    <Label>Frequency</Label>
                    <Select value={form.frequency} onValueChange={(v)=>setForm({...form, frequency: v})}>
                      <SelectTrigger data-testid="bill-form-frequency-select"><SelectValue/></SelectTrigger>
                      <SelectContent>{FREQUENCIES.map((f)=><SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <div className="text-sm font-medium">Auto-pay</div>
                    <div className="text-xs text-muted-foreground">Auto-mark as paid on due date</div>
                  </div>
                  <Switch checked={form.autopay} onCheckedChange={(v)=>setForm({...form, autopay: v})} data-testid="bill-form-autopay"/>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="bill-form-notes" rows={2}/>
                </div>
                <Button type="submit" className="w-full" data-testid="bill-form-submit">{editing ? "Save changes" : "Add bill"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-sm text-muted-foreground py-16 border border-dashed rounded-xl" data-testid="bills-empty">
            No bills yet. Add rent, subscriptions, groceries…
          </div>
        )}
        {filtered.map((b) => (
          <Card key={b.bill_id} data-testid={`bill-card-${b.bill_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{b.category}</div>
                  <div className="font-display text-xl font-bold mt-1">{b.name}</div>
                </div>
                <div className="tabular font-bold text-[hsl(var(--due-text))]">{formatMoney(b.amount, currency)}</div>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">
                {FREQUENCIES.find((f) => f.value === b.frequency)?.label} · starts {new Date(b.first_due_date).toLocaleDateString()}
              </div>
              {b.notes && <div className="mt-2 text-xs text-muted-foreground italic">"{b.notes}"</div>}
              <div className="mt-4 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={()=>openEdit(b)} data-testid={`bill-edit-${b.bill_id}`}><Pencil size={12}/></Button>
                <Button size="sm" variant="outline" onClick={()=>remove(b.bill_id)} data-testid={`bill-delete-${b.bill_id}`}><Trash2 size={12}/></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
